/**
 * Quantum Design System Theme Manager
 * 
 * This module provides utilities for managing the theme system
 * including setting the theme, detecting system preferences,
 * and handling theme-related DOM operations.
 */

import { QdsSettings } from './settings.js';
import { Theme, ThemeChangeEventDetail } from '../../types/index';

/**
 * Default theme options
 */
const THEME_OPTIONS = {
  LIGHT: 'light' as Theme,
  DARK: 'dark' as Theme,
  SYSTEM: 'system' as Theme,
};

/**
 * Settings keys
 */
const SETTINGS = {
  THEME: 'theme',
  AUTO_THEME: 'autoTheme',
};

/**
 * Theme management for the Quantum Design System
 * Provides methods to apply, change, and detect themes
 */
export class QdsThemeManager {
  private _settings: QdsSettings;
  private _currentTheme: Theme;
  private _autoTheme: boolean;
  private _mediaQuery: MediaQueryList;
  private _boundHandleSystemThemeChange: (event: MediaQueryListEvent) => void;

  /**
   * Creates a new theme manager instance
   */
  constructor() {
    // Create settings manager
    this._settings = new QdsSettings();
    
    // Initialize properties
    this._currentTheme = THEME_OPTIONS.LIGHT;
    this._autoTheme = true;
    this._mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    // Initialize from settings
    this._initialize();
    
    // Bind methods for event handlers
    this._boundHandleSystemThemeChange = this._handleSystemThemeChange.bind(this);
    
    // Add system preference listener
    this._mediaQuery.addEventListener('change', this._boundHandleSystemThemeChange);
  }
  
  /**
   * Initialize theme settings from stored preferences
   * @private
   */
  private _initialize(): void {
    const storedAutoTheme = this._settings.getSetting<boolean>(SETTINGS.AUTO_THEME, null);
    const storedTheme = this._settings.getSetting<Theme>(SETTINGS.THEME, null);

    if (storedAutoTheme === true || storedTheme === THEME_OPTIONS.SYSTEM) {
      this._autoTheme = true;
      this._currentTheme = this._getSystemPreference();
    } else if (
      storedAutoTheme === false &&
      (storedTheme === THEME_OPTIONS.LIGHT || storedTheme === THEME_OPTIONS.DARK)
    ) {
      this._autoTheme = false;
      this._currentTheme = storedTheme;
    } else if (storedTheme === THEME_OPTIONS.LIGHT || storedTheme === THEME_OPTIONS.DARK) {
      // Backward compatibility for environments where only the theme value was stored.
      this._autoTheme = false;
      this._currentTheme = storedTheme;
    } else {
      this._autoTheme = true;
      this._currentTheme = this._getSystemPreference();
      this._persistThemePreference(THEME_OPTIONS.SYSTEM, true);
    }

    this._applyTheme(this._currentTheme);
  }
  
  /**
   * Get the system color scheme preference
   * @private
   * @returns {Theme} The system preference (light or dark)
   */
  private _getSystemPreference(): Theme {
    return this._mediaQuery.matches ? THEME_OPTIONS.DARK : THEME_OPTIONS.LIGHT;
  }
  
  /**
   * Handle system theme change events
   * @private
   * @param {MediaQueryListEvent} event - The media query change event
   */
  private _handleSystemThemeChange(event: MediaQueryListEvent): void {
    if (!this._autoTheme) {
      return;
    }

    const newTheme = event.matches ? THEME_OPTIONS.DARK : THEME_OPTIONS.LIGHT;
    this._applyTheme(newTheme);
    this._currentTheme = newTheme;
  }

  /**
   * Persist the current theme preference.
   * @private
   * @param {Theme} theme - The saved theme preference
   * @param {boolean} autoTheme - Whether auto theme is enabled
   */
  private _persistThemePreference(theme: Theme, autoTheme: boolean): void {
    this._settings.saveSetting(SETTINGS.THEME, theme);
    this._settings.saveSetting(SETTINGS.AUTO_THEME, autoTheme);
  }
  
  /**
   * Apply the specified theme to the document
   * @private
   * @param {Theme} theme - The theme to apply ('light' or 'dark')
   */
  private _applyTheme(theme: Theme): void {
    // Set data-theme attribute on document.documentElement (html element)
    if (theme === THEME_OPTIONS.DARK) {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
    
    // Dispatch theme change event for components to listen to
    const detail: ThemeChangeEventDetail = { 
      theme: theme,
      dark: theme === THEME_OPTIONS.DARK,
      auto: this._autoTheme
    };
    
    const event = new CustomEvent<ThemeChangeEventDetail>('qds-theme-change', {
      bubbles: true,
      composed: true,
      detail
    });
    
    document.dispatchEvent(event);
  }
  
  /**
   * Get the current theme
   * @returns {Theme} The current theme ('light' or 'dark')
   */
  getTheme(): Theme {
    return this._currentTheme;
  }
  
  /**
   * Check if the current theme is dark
   * @returns {boolean} True if the current theme is dark
   */
  isDarkTheme(): boolean {
    return this._currentTheme === THEME_OPTIONS.DARK;
  }
  
  /**
   * Check if auto theme is enabled
   * @returns {boolean} True if auto theme is enabled
   */
  isAutoTheme(): boolean {
    return this._autoTheme;
  }
  
  /**
   * Set the theme
   * @param {Theme} theme - The theme to set ('light' or 'dark')
   * @returns {boolean} True if the theme was set successfully
   */
  setTheme(theme: Theme): boolean {
    // Validate theme
    if (theme !== THEME_OPTIONS.LIGHT && theme !== THEME_OPTIONS.DARK) {
      console.error(`Invalid theme: ${theme}. Expected 'light' or 'dark'.`);
      return false;
    }
    
    this._autoTheme = false;
    this._currentTheme = theme;
    this._persistThemePreference(theme, false);
    
    // Apply the theme
    this._applyTheme(theme);
    
    return true;
  }
  
  /**
   * Toggle between light and dark themes
   * @returns {Theme} The new theme
   */
  toggleTheme(): Theme {
    const newTheme = this._currentTheme === THEME_OPTIONS.LIGHT
      ? THEME_OPTIONS.DARK
      : THEME_OPTIONS.LIGHT;
    
    this.setTheme(newTheme);
    return newTheme;
  }
  
  /**
   * Enable automatic theme based on system preference
   * @returns {Theme} The applied theme
   */
  enableAutoTheme(): Theme {
    this._autoTheme = true;
    this._currentTheme = this._getSystemPreference();
    this._persistThemePreference(THEME_OPTIONS.SYSTEM, true);
    this._applyTheme(this._currentTheme);
    return this._currentTheme;
  }
  
  /**
   * Clean up event listeners
   */
  dispose(): void {
    this._mediaQuery.removeEventListener('change', this._boundHandleSystemThemeChange);
  }
}

// Create a singleton instance for easy importing
const themeManager = new QdsThemeManager();

export default themeManager;
